import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { anonymizePerson, type Person, type PersonCard } from '@/api/people';
import { queryKeyRoots } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/Dialog';

import { onDateTime } from './cardDates';

interface Props {
  person: PersonCard;
  onClose: () => void;
}

/**
 * §6.5's **Anonymize** — the product's only erasure path (ADR 0009, **feedback protection #6**), and
 * the single most consequential control in the product: **irreversible, synchronous, Admin only, and
 * there is no undo anywhere** — not on this screen, not through the API, not in the Django admin
 * (`PersonAdmin` refuses add and change), not for a superuser.
 *
 * ⚠️ **What the confirmation has to say, and why a bare "Are you sure?" would be wrong here.** The
 * erasure clears the name, the contacts, the source — with the three normalised duplicate-matching
 * twins derived from them — **and the text of every per-question and overall comment**, across every
 * interview of every consideration, drafts and submitted feedback alike, **and everything ADP sent
 * about this candidate**: every imported comment, and every imported document, erased from storage
 * (ADR 0020 decision 3). What it keeps is ADR 0009's entire argument: every rating, every verdict,
 * every `submitted_at`, every `question_text_snapshot`, every interview and score **row**, every
 * consideration, every status and every status-change log row. Nothing is taken out of the record
 * and **no count changes.** A confirmation that said only *"this cannot be undone"* would leave the
 * reader to guess which of those two lists their candidate's assessment history is on — and the
 * guess a red button invites is the wrong one. So the dialog states **both lists in words**, before
 * the click.
 *
 * ⚠️ **This writes to submitted feedback on purpose, and the pointer is owed to whoever asks.** ADR
 * 0009 outranks ADR 0008's immutability, and only here: the two protect different people against
 * different things (0008 protects the record against an author changing their mind; erasure serves a
 * data subject who is not a participant at all), nothing 0008 guards is altered, and the alternative
 * is a system that cannot satisfy a lawful request at all. `0009-anonymize-not-delete.md` →
 * *"Erasure versus immutability"* carries the argument; the screen does not need to know it, a
 * reviewer does.
 *
 * ⚠️ **An acknowledgement rather than a typed confirmation, decided here.** §9 requires only that the
 * destructive button is not autofocused (`Dialog` prevents `onOpenAutoFocus`, so focus lands on the
 * dialog and *Anonymize* is never one Enter away). With no undo at all, one deliberate extra act is
 * worth its friction — and the choice between the two patterns is not a matter of taste on **this**
 * screen:
 *
 *  1. **A typed name would be unfillable exactly where it is needed most.** The action stays offered
 *     on an already-anonymised person, because a second call is the documented remedy if text ever
 *     arrives after an erasure — and by then the name is `''`. A confirmation the operator cannot
 *     complete on the one card that needs a second pass is not a safeguard.
 *  2. **Retyping the name would put the personal data back on the screen that exists to erase it**,
 *     into an input the browser may remember, on the erasure request of the person who asked for it
 *     to be gone. Asking someone to type a candidate's name to prove they mean to erase it is the one
 *     interaction this feature must not have.
 *
 * So the acknowledgement is a checkbox that gates the button, and it names the property that has no
 * remedy — *immediately, and cannot be undone*. Note this is **not** §5's "hidden, never disabled"
 * rule being broken: that rule is about a **capability a group does not have** (which is why a
 * Recruiter is shown no item at all, `mayAnonymizePerson`), while this is a step inside a
 * confirmation the viewer is entitled to complete.
 *
 * ⚠️ **Invalidate and refetch — never write the answer into the card's cache.** The endpoint answers
 * `PersonOut`: the person alone, with no derived value, no consideration and no interview. Both roots
 * go, and neither is optional: §6.3's table infers an anonymised row from a **blank
 * `person_full_name`** (`ConsiderationTableRowOut` carries no `anonymized_at`), so a stale list after
 * an erasure would show the old name in the row next to the erased card.
 */
export function AnonymizeDialog({ person, onClose }: Props) {
  const { t, i18n } = useTranslation(['candidates', 'common', 'components']);
  const queryClient = useQueryClient();

  /**
   * The acknowledgement, held here and nowhere else: the dialog is mounted when it opens and
   * unmounted when it closes (`CardMenu`), so a cancelled confirmation cannot leave a ticked box
   * behind for the next time the menu is opened.
   */
  const [acknowledged, setAcknowledged] = useState(false);
  const acknowledgeId = useId();

  const erase = useMutation<Person, ApiError, void>({
    mutationFn: () => anonymizePerson(person.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.considerations });
      /**
       * ⚠️ **Removed, not invalidated, and this is the third family** — a sprint-end review finding
       * both `code-reviewer` and `security-auditor` reached independently (2026-07-28). §6.4's
       * duplicate pre-check caches a whole `PossibleDuplicateOut` — name, email, phone and source —
       * under a key that **is** the name that was typed to find it, and the two invalidations above
       * did not touch it. After an erasure the entry survived: reopening §6.4 and retyping the name
       * re-offered the erased person with **no request**, with a link to her card and *"Add a new
       * consideration to the existing card"* — which the server now refuses with the anonymised 409.
       * The server erasure was intact and the browser was still showing what it erased, which is ADR
       * 0009's central claim failing on the client. `invalidateQueries` would be no fix: it keeps the
       * body and refetches it on the next mount, and an erased key must be neither kept nor asked
       * for again.
       */
      queryClient.removeQueries({ queryKey: queryKeyRoots.duplicateCheck });
      onClose();
    },
  });

  /**
   * Three sentences, and there is deliberately **no fourth error path**: this operation declares
   * **only a 200** in the OpenAPI document — no refusal body of any kind — so there is no field for
   * `ApiError.bodyWith` to discriminate on, and inventing one would be a shape the API does not have.
   * Every sentence is the screen's own, never the server's `detail` and never a status code (§8, §11).
   *
   * ⚠️ **Offline gets this screen's own sentence rather than either shared one.**
   * `components:state.offline` **read** *"Changes will be sent when the connection returns"* until
   * BUG-017 split it: it is now the failed-**read** sentence, and a failed write says
   * `state.offlineWrite`, which denies the queue in so many words. Nothing queues this request, then or
   * now.
   *
   * **Why this screen still keeps its own** rather than adopting the shared write sentence: this write
   * is the one act with **no undo** (ADR 0009), so the reader may not be sent to a generic *"reload the
   * page"* — status 0 means **no answer arrived**, the client does not know whether the erasure ran, and
   * what is needed is the check that settles it on **this card**: *"if it shows no erasure banner,
   * nothing was cleared"*. That is the half a string shared with four other writes cannot carry, and on
   * an irreversible action it is the difference between a reader who verifies and one who guesses.
   * Reloading is safe advice precisely because a second call is idempotent and keeps the original
   * `anonymized_at`.
   *
   * A real status is different: the whole erasure is **one transaction** behind the person row's lock
   * (`apps.people.services.anonymize_person`), so a 4xx or a 5xx means it rolled back and *"nothing
   * was cleared"* is a claim that holds.
   */
  const failure = (() => {
    if (!erase.error) return null;
    if (erase.error.isOffline) return t('candidates:card.anonymize.offline');
    if (erase.error.isThrottled) return t('components:state.throttled');
    return t('candidates:card.anonymize.failed');
  })();

  const name = person.full_name.trim();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Esc and the overlay cancel, and cancelling writes nothing — Radix owns the focus trap and
        // the key handling (§9), and nothing here defeats it.
        if (!next) onClose();
      }}
      // Named where there is a name, because the failure mode of an irreversible action is doing it
      // to the wrong row — and unnamed where there is none, since *"Anonymize Anonymized?"* is what
      // the fallback display name would produce on a card that has already been erased.
      title={
        name === ''
          ? t('candidates:card.anonymize.title')
          : t('candidates:card.anonymize.titleNamed', { name })
      }
      description={t('candidates:card.anonymize.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          {/* §4: `danger` is outlined in `negative` and never filled — a filled warm button is the
              primary action on every other screen, and this is not a primary action. */}
          <Button
            variant="danger"
            disabled={!acknowledged}
            isLoading={erase.isPending}
            onClick={() => erase.mutate()}
          >
            {t('candidates:card.anonymize.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-ink-900">
        <p>
          <Lead>{t('candidates:card.anonymize.clearedLabel')}</Lead>{' '}
          {t('candidates:card.anonymize.cleared')}
        </p>

        {/* The half that is the ADR's whole argument, and it is stated as plainly as the other. */}
        <p>
          <Lead>{t('candidates:card.anonymize.keptLabel')}</Lead>{' '}
          {t('candidates:card.anonymize.kept')}
        </p>

        <p>
          <Lead>{t('candidates:card.anonymize.undoLabel')}</Lead>{' '}
          {t('candidates:card.anonymize.undo')}
        </p>

        {/*
         * ⚠️ Only on a card that has already been erased, and it is the reason the item is still
         * offered there: the call is **idempotent** and keeps the original `anonymized_at`, so a
         * second pass is the documented remedy if text ever arrives about an erased person. Saying so
         * here is what stops the repeat from reading as a mistake about to be made twice.
         */}
        {person.anonymized_at !== null && (
          <p className="text-ink-700">
            {t('candidates:card.anonymize.again', {
              date: onDateTime(person.anonymized_at, i18n.language),
            })}
          </p>
        )}

        <Checkbox
          id={acknowledgeId}
          checked={acknowledged}
          onCheckedChange={(next) => setAcknowledged(next === true)}
          label={t('candidates:card.anonymize.acknowledge')}
        />

        {failure && (
          <p role="alert" className="text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** The three lead-ins. Weight rather than colour, so the three paragraphs scan as a list of three. */
function Lead({ children }: { children: string }) {
  return <span className="font-semibold">{children}</span>;
}
